import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Get_type_code_options, Get_today_range, Get_yesterday_range, Get_this_week_range, Get_last_week_range, Get_this_month_range } from '../Comun';
import { AuthContext } from '../contexts/AuthContext';
import '../FilterModal.css';
import { apiFetch } from '../utils/apiFetch';

interface Event {
    id: number;
    name: string;
}

interface Tag {
    id: number;
    tag: string;
}

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyFilters: (filters: {
        id_client: number | null;
        id_event: number | null;
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }) => void;
    onClearFilters: () => void;
    clients: { id: number; name: string }[];
    currentFilters: {
        id_client: number | null;
        id_event: number | null;
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    };
}

const FilterEscaneoModal: React.FC<FilterModalProps> = ({
    isOpen,
    onClose,
    onApplyFilters,
    onClearFilters,
    clients,
    currentFilters }) =>
{
    const { token } = useContext(AuthContext);
    const [events, setEvents] = useState<Event[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [idClient, setIdClient] = useState<number | null>(currentFilters.id_client);
    const [idEvent, setIdEvent] = useState<number | null>(currentFilters.id_event);
    const [isRootResponse, setIsRootResponse] = useState<boolean>(currentFilters.is_root_response);
    const [response, setResponse] = useState<string | null>(currentFilters.response);
    const [code, setCode] = useState<string | null>(currentFilters.code);
    const [type, setType] = useState<number | null>(currentFilters.type);
    const [idTag, setIdTag] = useState<number | null>(currentFilters.id_tag);
    const [accepted, setAccepted] = useState<number | null>(currentFilters.accepted);
    const [startDT, setStartDT] = useState<string | null>(currentFilters.start_dt);
    const [endDT, setEndDT] = useState<string | null>(currentFilters.end_dt);
    const [dateRange, setDateRange] = useState<string>('--Todas las fechas--');
    const modalRef = useRef<HTMLDivElement>(null);

    const typeOptions = Get_type_code_options();

    const fetchEvents = useCallback(async () =>
    {
        try
        {
            setError(null);
            const response = await apiFetch(`web/get_client_events?id_client=${idClient}`, { method: 'GET' });
            if (response.ok)
            {
                const data = await response.json();
                setEvents(data.events);
            }
            else throw new Error('Error al cargar eventos.');
        }
        catch (error)
        {
            setError(error instanceof Error ? error.message : 'Error desconocido');
        }
    }, [idClient, token]);

    const fetchTags = useCallback(async () =>
    {
        try
        {
            setError(null);
            const response = await apiFetch(`web/get_event_tags?id_event=${idEvent}&id_client=${idClient}`, { method: 'GET' });
            if (response.ok)
            {
                const data = await response.json();
                setTags(data.tags);
            }
            else throw new Error('Error al cargar tags.');
        }
        catch (error)
        {
            setError(error instanceof Error ? error.message : 'Error desconocido');
        }
    }, [idEvent, token]);

    useEffect(() =>
    {
        if (idClient !== null) fetchEvents();
        else { setEvents([]); setIdEvent(null); setIdTag(null); }
    }, [idClient, fetchEvents]);

    useEffect(() =>
    {
        if (idEvent !== null) fetchTags();
        else { setTags([]); setIdTag(null); }
    }, [idEvent, fetchTags]);

    useEffect(() =>
    {
        if (isOpen)
        {
            setIdClient(currentFilters.id_client);
            setIdEvent(currentFilters.id_event);
            setIsRootResponse(currentFilters.is_root_response);
            setResponse(currentFilters.response);
            setCode(currentFilters.code);
            setType(currentFilters.type);
            setIdTag(currentFilters.id_tag);
            setAccepted(currentFilters.accepted);
            setStartDT(currentFilters.start_dt);
            setEndDT(currentFilters.end_dt);

            if (currentFilters.start_dt === null && currentFilters.end_dt === null)
            {
                setDateRange('--Todas las fechas--');
            }
            else if (currentFilters.start_dt && currentFilters.end_dt)
            {
                let s: string | null = null, e: string | null = null;
                ({ start_dt: s, end_dt: e } = Get_today_range());
                if (s === currentFilters.start_dt && e === currentFilters.end_dt) { setDateRange('Hoy'); return; }
                ({ start_dt: s, end_dt: e } = Get_yesterday_range());
                if (s === currentFilters.start_dt && e === currentFilters.end_dt) { setDateRange('Ayer'); return; }
                ({ start_dt: s, end_dt: e } = Get_this_week_range());
                if (s === currentFilters.start_dt && e === currentFilters.end_dt) { setDateRange('Esta semana'); return; }
                ({ start_dt: s, end_dt: e } = Get_last_week_range());
                if (s === currentFilters.start_dt && e === currentFilters.end_dt) { setDateRange('Semana pasada'); return; }
                ({ start_dt: s, end_dt: e } = Get_this_month_range());
                if (s === currentFilters.start_dt && e === currentFilters.end_dt) { setDateRange('Este mes'); return; }
                setDateRange('Personalizado');
            }
            else setDateRange('Personalizado');
        }
    }, [isOpen, currentFilters]);

    useEffect(() =>
    {
        let s: string | null = null, e: string | null = null;
        switch (dateRange)
        {
            case '--Todas las fechas--': setStartDT(null); setEndDT(null); return;
            case 'Hoy': ({ start_dt: s, end_dt: e } = Get_today_range()); break;
            case 'Ayer': ({ start_dt: s, end_dt: e } = Get_yesterday_range()); break;
            case 'Esta semana': ({ start_dt: s, end_dt: e } = Get_this_week_range()); break;
            case 'Semana pasada': ({ start_dt: s, end_dt: e } = Get_last_week_range()); break;
            case 'Este mes': ({ start_dt: s, end_dt: e } = Get_this_month_range()); break;
            case 'Personalizado': s = currentFilters.start_dt; e = currentFilters.end_dt; break;
            default: setStartDT(null); setEndDT(null); return;
        }
        if (s !== null && e !== null) { setStartDT(s); setEndDT(e); }
    }, [dateRange, currentFilters.start_dt, currentFilters.end_dt]);

    useEffect(() =>
    {
        const handleClickOutside = (event: MouseEvent) =>
        {
            if (modalRef.current && !modalRef.current.contains(event.target as Node))
                onClose();
        };
        const handleEscPress = (event: KeyboardEvent) =>
        {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscPress);
        return () =>
        {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscPress);
        };
    }, [onClose]);

    const handleApplyFilters = () =>
    {
        onApplyFilters({ id_client: idClient, id_event: idEvent, is_root_response: isRootResponse, response, code, type, id_tag: idTag, accepted, start_dt: startDT, end_dt: endDT });
        onClose();
    };

    const handleClearFilters = () =>
    {
        setIdClient(null); setIdEvent(null); setIsRootResponse(false);
        setResponse(null); setCode(null); setType(null);
        setIdTag(null); setAccepted(null); setStartDT(null);
        setEndDT(null); setDateRange('--Todas las fechas--');
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" ref={modalRef}>
                <h2>Filtrar escaneos</h2>
                <form>

                    {/* Recinto */}
                    <div className="form-group">
                        <select
                            value={idClient !== null ? idClient : ''}
                            onChange={(e) => setIdClient(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Todos los recintos</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Evento */}
                    <div className="form-group">
                        <select
                            value={idEvent !== null ? idEvent : ''}
                            onChange={(e) => setIdEvent(e.target.value ? Number(e.target.value) : null)}
                            disabled={idClient === null}
                        >
                            <option value="">Todos los eventos</option>
                            {events.map(event => (
                                <option key={event.id} value={event.id}>{event.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Código */}
                    <div className="form-group">
                        <input
                            type="text"
                            value={code || ''}
                            placeholder="Todos los códigos"
                            onChange={(e) => setCode(e.target.value || null)}
                        />
                    </div>

                    {/* Tipo búsqueda + respuesta */}
                    <div className="form-group form-group--row">
                        <select
                            className="select-inline"
                            value={isRootResponse ? 'startsWith' : 'contains'}
                            onChange={(e) => setIsRootResponse(e.target.value === 'startsWith')}
                        >
                            <option value="startsWith">Empieza con</option>
                            <option value="contains">Contiene</option>
                        </select>
                        <input
                            type="text"
                            className="input-response"
                            value={response || ''}
                            placeholder="Todas las respuestas"
                            onChange={(e) => setResponse(e.target.value || null)}
                        />
                    </div>

                    {/* Tipo */}
                    <div className="form-group">
                        <select
                            value={type !== null ? type : ''}
                            onChange={(e) => setType(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Todos los tipos</option>
                            {typeOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Etiqueta */}
                    <div className="form-group">
                        <select
                            value={idTag !== null ? idTag : ''}
                            onChange={(e) => setIdTag(e.target.value ? Number(e.target.value) : null)}
                            disabled={idEvent === null}
                        >
                            <option value="">Todas las etiquetas</option>
                            {tags.map(tag => (
                                <option key={tag.id} value={tag.id}>{tag.tag}</option>
                            ))}
                        </select>
                    </div>

                    {/* Estado */}
                    <div className="form-group">
                        <select
                            value={accepted !== null ? accepted : ''}
                            onChange={(e) => setAccepted(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Todos los estados</option>
                            <option value="0" className="red">Rechazado</option>
                            <option value="1" className="green">Aceptado</option>
                        </select>
                    </div>

                    {/* Rango fechas */}
                    <div className="form-group">
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <option value="--Todas las fechas--">Todas las fechas</option>
                            <option value="Hoy">Hoy</option>
                            <option value="Ayer">Ayer</option>
                            <option value="Esta semana">Esta semana</option>
                            <option value="Semana pasada">Semana pasada</option>
                            <option value="Este mes">Este mes</option>
                            <option value="Personalizado">Personalizado</option>
                        </select>
                    </div>

                    {/* Fechas personalizadas */}
                    <div className={`form-group date-range-container ${dateRange !== 'Personalizado' ? 'hidden' : ''}`}>
                        <input
                            type="datetime-local"
                            value={startDT || ''}
                            onChange={(e) => setStartDT(e.target.value || null)}
                        />
                        <input
                            type="datetime-local"
                            value={endDT || ''}
                            onChange={(e) => setEndDT(e.target.value || null)}
                        />
                    </div>

                    {error && <p className="error-message">{error}</p>}

                    {/* Acciones */}
                    <div className="modal-actions">
                        <button type="button" className="delete-filter-button" onClick={handleClearFilters}>
                            Borrar filtros
                        </button>
                        <div className="modal-actions-right">
                            <button type="button" className="filter-button" onClick={onClose}>Cancelar</button>
                            <button type="button" className="filter-button2" onClick={handleApplyFilters}>Aplicar</button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default FilterEscaneoModal;